import React, { useState } from "react";
import {
    Eye,
    Pencil,
    Trash2,
    GripVertical,
    Circle,
    CheckCircle,
    Pause,
    Play,
    MoreHorizontal,
} from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import PauseReasonModal from "./PauseReasonModal";
import CompleteProofModal from "./CompleteProofModal";
import { STAFF_PAUSE_REASONS } from "../constants";
import { formatDueLabel, isOverdueDueLabel } from "../utils/taskDates";
import { isRecurringTask } from "../utils/taskReminderSync";

const touchBtn = "min-h-[44px] min-w-[44px] flex items-center justify-center rounded-xl transition";

const TaskRow = ({
    task,
    role,
    onComplete,
    onEdit,
    onDelete,
    onView,
    onTogglePause,
    selectable = false,
    selected = false,
    onSelectChange,
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: task.id });

    const [pauseModalOpen, setPauseModalOpen] = useState(false);
    const [completeModalOpen, setCompleteModalOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    const isStaff = role === "staff";
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const canEdit = role === "admin" || task.assigned_by === "Staff";
    const dueLabel = formatDueLabel(task);
    const priorityClass =
        task.priority === "high"
            ? "bg-error/20 text-error"
            : task.priority === "medium"
              ? "bg-warning/20 text-warning"
              : "bg-success/20 text-success";

    const handleCompleteClick = () => {
        if (task.status === "Completed") {
            onComplete(task.id, task.status);
            return;
        }
        if (isStaff) {
            setCompleteModalOpen(true);
        } else {
            onComplete(task.id, task.status);
        }
    };

    return (
        <>
            <div
                ref={setNodeRef}
                style={style}
                {...attributes}
                className={`flex items-center justify-between gap-2 bg-surface-2/80 backdrop-blur-sm rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-line ${
                    isStaff ? "p-3" : "p-2 items-start"
                } ${menuOpen ? "relative z-50" : ""}`}
            >
                <div className="flex gap-2 flex-1 min-w-0 items-center">
                    {selectable && (
                        <input
                            type="checkbox"
                            checked={selected}
                            onChange={(e) => onSelectChange?.(task.id, e.target.checked)}
                            className="h-4 w-4 shrink-0 rounded border-line accent-primary cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                        />
                    )}
                    {!isStaff && (
                        <button
                            {...listeners}
                            type="button"
                            className="cursor-grab active:cursor-grabbing text-ink-muted hover:text-ink shrink-0"
                            title="Drag to reorder"
                        >
                            <GripVertical size={16} />
                        </button>
                    )}
                    {isStaff && (
                        <button
                            {...listeners}
                            type="button"
                            className={`${touchBtn} text-ink-muted shrink-0 -ml-1`}
                            title="Drag to reorder"
                        >
                            <GripVertical size={18} />
                        </button>
                    )}

                    <button
                        type="button"
                        className="flex-1 min-w-0 text-left"
                        onClick={() => onView(task)}
                    >
                        <span
                            style={{
                                textDecoration:
                                    task.status === "Completed" ? "line-through" : "",
                            }}
                            className={`font-semibold text-ink block truncate ${isStaff ? "text-sm" : "text-xs"}`}
                        >
                            {task.title}
                        </span>
                        <div className="flex items-center gap-2 text-xs flex-wrap mt-1">
                            <span
                                className={`px-2 py-0.5 rounded-full font-medium capitalize ${priorityClass}`}
                            >
                                {task.priority}
                            </span>
                            {dueLabel && (
                                <span
                                    className={`font-medium ${
                                        isOverdueDueLabel(dueLabel) ? "text-error" : "text-ink-muted"
                                    }`}
                                >
                                    {dueLabel}
                                </span>
                            )}
                            {isStaff && task.assigned_by === "Admin" && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-soft text-primary">
                                    From admin
                                </span>
                            )}
                        </div>
                    </button>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                    {task.status !== "Completed" &&
                        (task.paused ? (
                            <button
                                type="button"
                                onClick={() => onTogglePause(task.id, task.paused)}
                                className={`${touchBtn} hover:bg-success/10 text-success`}
                                title="Continue task"
                            >
                                <Play size={isStaff ? 22 : 16} />
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setPauseModalOpen(true)}
                                className={`${touchBtn} hover:bg-warning/10 text-warning`}
                                title="Pause task"
                            >
                                <Pause size={isStaff ? 22 : 16} />
                            </button>
                        ))}

                    <button
                        type="button"
                        onClick={handleCompleteClick}
                        className={`${touchBtn} hover:bg-success/10`}
                        title={
                            task.status === "Completed"
                                ? "Mark incomplete"
                                : "Mark complete"
                        }
                    >
                        {task.status === "Completed" ? (
                            <CheckCircle size={isStaff ? 24 : 20} className="text-success" />
                        ) : (
                            <Circle
                                size={isStaff ? 24 : 20}
                                className="text-ink-muted hover:text-success"
                            />
                        )}
                    </button>

                    {isStaff ? (
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setMenuOpen((p) => !p)}
                                className={`${touchBtn} hover:bg-surface-3 text-ink-secondary`}
                            >
                                <MoreHorizontal size={20} />
                            </button>
                            {menuOpen && (
                                <>
                                    <div
                                        className="fixed inset-0 z-[99]"
                                        onClick={() => setMenuOpen(false)}
                                    />
                                    <div className="absolute right-0 top-full mt-1 z-[100] w-40 rounded-xl border border-line bg-surface shadow-lg py-1">
                                        {canEdit && (
                                            <button
                                                type="button"
                                                className="w-full px-4 py-3 text-left text-sm text-ink hover:bg-surface-2 flex items-center gap-2 min-h-[44px]"
                                                onClick={() => {
                                                    setMenuOpen(false);
                                                    onEdit(task);
                                                }}
                                            >
                                                <Pencil size={16} /> Edit
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="w-full px-4 py-3 text-left text-sm text-error hover:bg-error/10 flex items-center gap-2 min-h-[44px]"
                                            onClick={() => {
                                                setMenuOpen(false);
                                                onDelete(task.id);
                                            }}
                                        >
                                            <Trash2 size={16} /> Delete
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="hidden sm:flex items-center gap-1">
                                <button
                                    type="button"
                                    className="p-1 hover:bg-surface-3 rounded-md transition min-h-[32px] min-w-[32px] flex items-center justify-center"
                                    onClick={() => onView(task)}
                                    title="View task"
                                >
                                    <Eye size={14} className="text-ink-secondary" />
                                </button>
                                {canEdit && (
                                    <button
                                        type="button"
                                        onClick={() => onEdit(task)}
                                        className="p-1 hover:bg-primary-soft rounded-md transition min-h-[32px] min-w-[32px] flex items-center justify-center"
                                        title="Edit task"
                                    >
                                        <Pencil size={14} className="text-primary" />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => onDelete(task.id)}
                                    className="p-1 hover:bg-error/10 rounded-md transition min-h-[32px] min-w-[32px] flex items-center justify-center"
                                    title="Delete task"
                                >
                                    <Trash2 size={14} className="text-error" />
                                </button>
                            </div>
                            <div className="relative sm:hidden">
                                <button
                                    type="button"
                                    onClick={() => setMenuOpen((p) => !p)}
                                    className={`${touchBtn} hover:bg-surface-3 text-ink-secondary`}
                                >
                                    <MoreHorizontal size={20} />
                                </button>
                                {menuOpen && (
                                    <>
                                        <div
                                            className="fixed inset-0 z-[99]"
                                            onClick={() => setMenuOpen(false)}
                                        />
                                        <div className="absolute right-0 top-full mt-1 z-[100] w-40 rounded-xl border border-line bg-surface shadow-lg py-1">
                                            <button
                                                type="button"
                                                className="w-full px-4 py-3 text-left text-sm text-ink hover:bg-surface-2 flex items-center gap-2 min-h-[44px]"
                                                onClick={() => {
                                                    setMenuOpen(false);
                                                    onView(task);
                                                }}
                                            >
                                                <Eye size={16} /> View
                                            </button>
                                            {canEdit && (
                                                <button
                                                    type="button"
                                                    className="w-full px-4 py-3 text-left text-sm text-ink hover:bg-surface-2 flex items-center gap-2 min-h-[44px]"
                                                    onClick={() => {
                                                        setMenuOpen(false);
                                                        onEdit(task);
                                                    }}
                                                >
                                                    <Pencil size={16} /> Edit
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="w-full px-4 py-3 text-left text-sm text-error hover:bg-error/10 flex items-center gap-2 min-h-[44px]"
                                                onClick={() => {
                                                    setMenuOpen(false);
                                                    onDelete(task.id);
                                                }}
                                            >
                                                <Trash2 size={16} /> Delete
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <PauseReasonModal
                isOpen={pauseModalOpen}
                onClose={() => setPauseModalOpen(false)}
                onConfirm={(reason) => onTogglePause(task.id, task.paused, reason)}
                reasons={isStaff ? STAFF_PAUSE_REASONS : undefined}
                compact={isStaff}
            />

            {isStaff && (
                <CompleteProofModal
                    isOpen={completeModalOpen}
                    taskTitle={task.title}
                    isRecurring={isRecurringTask(task)}
                    onClose={() => setCompleteModalOpen(false)}
                    onConfirm={(file) => onComplete(task.id, task.status, file)}
                />
            )}
        </>
    );
};

export default TaskRow;
